import React, { useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, Divider, Stack,
  Grid, LinearProgress, Chip, Tooltip, IconButton,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import { DateField } from '@mui/x-date-pickers/DateField';
import { dbDate, parseDbDate } from '@caredna-marlin/utils/dates';
import { useNavigate } from 'react-router-dom';
import { useTransitionContext } from '~/hooks/transition/useTransitionContext';
import { FaxIntakeFormData } from '~/types/transition';

interface Props {
  open: boolean;
  onClose: () => void;
}

type Step = 'upload' | 'extracting' | 'review';

// Mock pre-extracted data — simulates what AI pulls from a fax PDF
const MOCK_EXTRACTED: Omit<FaxIntakeFormData, 'fileName' | 'fileUrl'> = {
  patientName: 'William Foster',
  dob: '1951-08-14',
  phone: '',
  address: '3421 Fremont Ave N',
  city: 'Seattle',
  state: 'WA',
  zip: '98103',
  diagnosisLabel: 'Hip Fracture, Post-Surgical',
  diagnosisCode: 'S72.001A',
  requestedProgram: 'Home Health',
  insurance: 'Medicare',
  insuranceMemberId: '',
  referringProvider: 'Dr. Michael Carr',
  referringFacility: 'Seattle Orthopedic Institute',
  referringPhone: '',
  referringFax: '(206) 555-0144',
};

// Fields the AI extracted with low confidence — coordinator must verify
const LOW_CONFIDENCE: Set<keyof FaxIntakeFormData> = new Set([]);

// Fields the AI could not extract — coordinator must fill in
const MISSING_EXTRACTED: Set<keyof FaxIntakeFormData> = new Set([
  'insuranceMemberId',
  'referringPhone',
  'phone',
]);

const REQUIRED: (keyof FaxIntakeFormData)[] = [
  'patientName',
  'dob',
  'diagnosisLabel',
  'requestedProgram',
  'insurance',
  'referringProvider',
  'referringFacility',
];

const ACCEPTED_TYPES = '.pdf,.jpg,.jpeg,.png,.tiff,.tif';

const SectionLabel: React.FC<{ children: string }> = ({ children }) => (
  <Typography
    variant="overline"
    color="text.secondary"
    fontWeight={600}
    sx={{ display: 'block', mb: 1.5, letterSpacing: 0.8 }}
  >
    {children}
  </Typography>
);

// Wraps a field to show a low-confidence or missing indicator
const FieldWrapper: React.FC<{
  fieldKey: keyof FaxIntakeFormData;
  children: React.ReactNode;
}> = ({ fieldKey, children }) => {
  const isLow = LOW_CONFIDENCE.has(fieldKey);
  const isMissing = MISSING_EXTRACTED.has(fieldKey);

  if (!isLow && !isMissing) return <>{children}</>;

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
            borderColor: isMissing ? 'error.light' : 'warning.main',
            borderWidth: 1.5,
          },
        }}
      >
        {children}
      </Box>
      <Tooltip
        title={
          isMissing
            ? 'AI could not extract this field — please fill in manually'
            : 'AI extracted this with low confidence — please verify'
        }
        placement="top-end"
      >
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            display: 'flex',
            alignItems: 'center',
            pointerEvents: 'none',
          }}
        >
          <WarningAmberIcon
            sx={{ fontSize: '0.9rem', color: isMissing ? 'error.main' : 'warning.main' }}
          />
        </Box>
      </Tooltip>
    </Box>
  );
};

const ZOOM_STEP = 0.25;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const PDF_BASE_HEIGHT = 820;

const FaxUploadDialog: React.FC<Props> = ({ open, onClose }) => {
  const { createFaxReferral } = useTransitionContext();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [fileName, setFileName] = useState('');
  const [fileUrl, setFileUrl] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [form, setForm] = useState<FaxIntakeFormData>({ ...MOCK_EXTRACTED, fileName: '', fileUrl: '' });
  const [submitted, setSubmitted] = useState(false);
  const [zoom, setZoom] = useState(1);

  const set = (field: keyof FaxIntakeFormData) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const filledRequired = REQUIRED.filter((f) => (form[f] as string).trim() !== '').length;
  const allRequiredFilled = filledRequired === REQUIRED.length;

  const missingCount = [...MISSING_EXTRACTED].filter(
    (f) => !(form[f] as string).trim()
  ).length;

  const handleFile = (file: File) => {
    setFileName(file.name);
    const url = URL.createObjectURL(file);
    setFileUrl(url);
    setStep('extracting');
    // Simulate AI extraction delay
    setTimeout(() => {
      setForm({ ...MOCK_EXTRACTED, fileName: file.name, fileUrl: url });
      setStep('review');
    }, 2200);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleSubmit = () => {
    if (!allRequiredFilled) return;
    const newId = createFaxReferral(form);
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      resetAndClose(false); // keep URL alive — stored on the referral record
      navigate(`/transition/referral/${newId}`);
    }, 1200);
  };

  const resetAndClose = (revokeUrl = true) => {
    setStep('upload');
    setFileName('');
    if (revokeUrl && fileUrl) URL.revokeObjectURL(fileUrl);
    setFileUrl('');
    setDragOver(false);
    setForm({ ...MOCK_EXTRACTED, fileName: '', fileUrl: '' });
    setSubmitted(false);
    setZoom(1);
    onClose();
  };

  // ── Success flash ─────────────────────────────────────────────────────────

  if (submitted) {
    return (
      <Dialog open={open} maxWidth="xs" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 5 }}>
          <CheckCircleIcon sx={{ fontSize: 52, color: 'success.main', mb: 1.5 }} />
          <Typography variant="h6" fontWeight={600}>Referral Created</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            AI processing has started. Opening the referral record now.
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  // ── Upload step ───────────────────────────────────────────────────────────

  if (step === 'upload') {
    return (
      <Dialog open={open} onClose={() => resetAndClose()} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ bgcolor: 'grey.100', borderRadius: '50%', p: 0.75, display: 'flex' }}>
              <UploadFileIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>New Referral from Document</Typography>
              <Typography variant="caption" color="text.secondary">
                Upload a fax, PDF, or image — AI will extract the referral data for your review
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Box
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              mt: 1,
              border: '2px dashed',
              borderColor: dragOver ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              p: 5,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: dragOver ? 'primary.50' : 'grey.50',
              transition: 'all 0.15s',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' },
            }}
          >
            <UploadFileIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" fontWeight={600} color="text.primary">
              Drag and drop a file, or click to browse
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              PDF, JPG, PNG, or TIFF — fax cover sheets included
            </Typography>
          </Box>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            style={{ display: 'none' }}
            onChange={handleFileInput}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => resetAndClose()} color="inherit">Cancel</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ── Extracting step ───────────────────────────────────────────────────────

  if (step === 'extracting') {
    return (
      <Dialog open={open} maxWidth="sm" fullWidth>
        <DialogContent sx={{ py: 5, textAlign: 'center' }}>
          <Stack direction="row" alignItems="center" spacing={1} justifyContent="center" sx={{ mb: 2 }}>
            <InsertDriveFileIcon sx={{ color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {fileName}
            </Typography>
          </Stack>
          <AutoAwesomeIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1.5 }} />
          <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
            Extracting referral data...
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            AI is reading the document and structuring the referral fields
          </Typography>
          <LinearProgress sx={{ borderRadius: 1, mx: 4 }} />
        </DialogContent>
      </Dialog>
    );
  }

  // ── Review step ───────────────────────────────────────────────────────────

  const lowConfidenceRemaining = [...LOW_CONFIDENCE].length;
  const missingRemaining = missingCount;

  return (
    <Dialog
      open={open}
      onClose={() => resetAndClose()}
      maxWidth={false}
      PaperProps={{ sx: { width: '92vw', maxWidth: 1400, height: '90vh', display: 'flex', flexDirection: 'column' } }}
    >
      <DialogTitle sx={{ pb: 1, flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ bgcolor: 'primary.50', borderRadius: '50%', p: 0.75, display: 'flex' }}>
              <AutoAwesomeIcon sx={{ color: 'primary.main', fontSize: '1.2rem' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>Review Extracted Data</Typography>
              <Typography variant="caption" color="text.secondary">From: {fileName}</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            {missingRemaining > 0 && (
              <Chip
                icon={<WarningAmberIcon />}
                label={`${missingRemaining} field${missingRemaining > 1 ? 's' : ''} not found`}
                size="small"
                color="error"
                variant="outlined"
                sx={{ fontSize: '0.75rem' }}
              />
            )}
            {lowConfidenceRemaining > 0 && (
              <Chip
                icon={<WarningAmberIcon />}
                label={`${lowConfidenceRemaining} low confidence`}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ fontSize: '0.75rem' }}
              />
            )}
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ flex: 1, display: 'flex', overflow: 'hidden', p: 0, gap: 0 }}
      >
        {/* LEFT: Form */}
        <Box sx={{ flex: '0 0 420px', overflowY: 'auto', p: 2.5 }}>
          <Stack spacing={0.75} sx={{ mb: 2.5 }}>
            <Typography variant="body2" color="text.secondary">
              AI has pre-filled the fields below. Fields marked{' '}
              <WarningAmberIcon sx={{ fontSize: '0.85rem', color: 'warning.main', verticalAlign: 'middle' }} />{' '}
              in amber were extracted with low confidence — verify before submitting. Fields marked{' '}
              <WarningAmberIcon sx={{ fontSize: '0.85rem', color: 'error.main', verticalAlign: 'middle' }} />{' '}
              in red could not be extracted and require manual entry.
            </Typography>
          </Stack>

          <Stack spacing={3}>
            {/* Patient */}
            <Box>
              <SectionLabel>Patient</SectionLabel>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <FieldWrapper fieldKey="patientName">
                    <TextField label="Patient Name *" fullWidth size="small" value={form.patientName} onChange={set('patientName')} />
                  </FieldWrapper>
                </Grid>
                <Grid item xs={4}>
                  <FieldWrapper fieldKey="dob">
                    <DateField
                      label="Date of Birth *"
                      fullWidth
                      size="small"
                      clearable
                      maxDate={new Date()}
                      value={form.dob ? parseDbDate(form.dob) : null}
                      onChange={(date) => {
                        const dob = date && !isNaN(date.getTime()) ? dbDate(date) : '';
                        setForm((prev) => ({ ...prev, dob }));
                      }}
                    />
                  </FieldWrapper>
                </Grid>
                <Grid item xs={4}>
                  <FieldWrapper fieldKey="phone">
                    <TextField label="Phone" fullWidth size="small" value={form.phone} onChange={set('phone')} />
                  </FieldWrapper>
                </Grid>
                <Grid item xs={8}>
                  <FieldWrapper fieldKey="address">
                    <TextField label="Street Address" fullWidth size="small" value={form.address} onChange={set('address')} />
                  </FieldWrapper>
                </Grid>
                <Grid item xs={5}>
                  <FieldWrapper fieldKey="city">
                    <TextField label="City" fullWidth size="small" value={form.city} onChange={set('city')} />
                  </FieldWrapper>
                </Grid>
                <Grid item xs={3}>
                  <FieldWrapper fieldKey="state">
                    <TextField label="State" fullWidth size="small" value={form.state} onChange={set('state')} inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }} />
                  </FieldWrapper>
                </Grid>
                <Grid item xs={4}>
                  <FieldWrapper fieldKey="zip">
                    <TextField label="Zip" fullWidth size="small" value={form.zip} onChange={set('zip')} />
                  </FieldWrapper>
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Clinical */}
            <Box>
              <SectionLabel>Clinical</SectionLabel>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <FieldWrapper fieldKey="diagnosisLabel">
                    <TextField label="Primary Diagnosis *" fullWidth size="small" value={form.diagnosisLabel} onChange={set('diagnosisLabel')} />
                  </FieldWrapper>
                </Grid>
                <Grid item xs={4}>
                  <FieldWrapper fieldKey="diagnosisCode">
                    <TextField label="ICD-10 Code" fullWidth size="small" value={form.diagnosisCode} onChange={set('diagnosisCode')} />
                  </FieldWrapper>
                </Grid>
                <Grid item xs={12}>
                  <FieldWrapper fieldKey="requestedProgram">
                    <TextField label="Requested Program *" fullWidth size="small" value={form.requestedProgram} onChange={set('requestedProgram')} />
                  </FieldWrapper>
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Insurance */}
            <Box>
              <SectionLabel>Insurance</SectionLabel>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FieldWrapper fieldKey="insurance">
                    <TextField label="Insurance / Payer *" fullWidth size="small" value={form.insurance} onChange={set('insurance')} />
                  </FieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <FieldWrapper fieldKey="insuranceMemberId">
                    <TextField label="Member ID" fullWidth size="small" value={form.insuranceMemberId} onChange={set('insuranceMemberId')} />
                  </FieldWrapper>
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Referring Provider */}
            <Box>
              <SectionLabel>Referring Provider</SectionLabel>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FieldWrapper fieldKey="referringProvider">
                    <TextField label="Provider Name *" fullWidth size="small" value={form.referringProvider} onChange={set('referringProvider')} />
                  </FieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <FieldWrapper fieldKey="referringFacility">
                    <TextField label="Facility *" fullWidth size="small" value={form.referringFacility} onChange={set('referringFacility')} />
                  </FieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <FieldWrapper fieldKey="referringPhone">
                    <TextField label="Provider Phone" fullWidth size="small" value={form.referringPhone} onChange={set('referringPhone')} />
                  </FieldWrapper>
                </Grid>
                <Grid item xs={6}>
                  <FieldWrapper fieldKey="referringFax">
                    <TextField label="Provider Fax" fullWidth size="small" value={form.referringFax} onChange={set('referringFax')} />
                  </FieldWrapper>
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </Box>

        <Divider orientation="vertical" flexItem />

        {/* RIGHT: Document viewer */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
          {/* Zoom toolbar */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ px: 2, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0 }}
          >
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mr: 'auto' }}>
              Document Preview
            </Typography>
            <Tooltip title="Zoom out">
              <span>
                <IconButton size="small" onClick={() => setZoom((z) => Math.max(MIN_ZOOM, parseFloat((z - ZOOM_STEP).toFixed(2))))} disabled={zoom <= MIN_ZOOM}>
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Typography variant="caption" sx={{ minWidth: 38, textAlign: 'center', fontWeight: 600 }}>
              {Math.round(zoom * 100)}%
            </Typography>
            <Tooltip title="Zoom in">
              <span>
                <IconButton size="small" onClick={() => setZoom((z) => Math.min(MAX_ZOOM, parseFloat((z + ZOOM_STEP).toFixed(2))))} disabled={zoom >= MAX_ZOOM}>
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Reset zoom">
              <span>
                <IconButton size="small" onClick={() => setZoom(1)} disabled={zoom === 1}>
                  <ZoomOutMapIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          {/* PDF area */}
          <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#525659' }}>
            {fileUrl ? (
              <iframe
                src={fileUrl}
                title="Referral document"
                style={{
                  display: 'block',
                  width: `${zoom * 100}%`,
                  height: `${Math.round(zoom * PDF_BASE_HEIGHT)}px`,
                  border: 'none',
                }}
              />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography variant="body2" color="grey.400">No document loaded</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexShrink: 0 }}>
        <Button onClick={() => resetAndClose()} color="inherit">Cancel</Button>
        <Button
          variant="contained"
          disabled={!allRequiredFilled}
          onClick={handleSubmit}
          sx={{ minWidth: 220 }}
        >
          Confirm &amp; Create Referral
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FaxUploadDialog;
